import React from "react";

/**
 * Athlete profile against the peer average across the sport's session metrics.
 *
 * A radar is the right shape here for the one reason radars are ever right: the
 * question is "what is this athlete's SHAPE" — fast but low-volume, or the
 * reverse — not "which axis is biggest". Every axis shares a 0-100 normalised
 * domain, which is the other precondition. The table beside it carries the real
 * values, so the polygon never has to be read precisely.
 */
const Radar = ({ values = [], reference = [], labels = [], size = 330 }) => {
  const axes = values.length;
  if (axes < 3) {
    return (
      <p className="py-8 text-center text-[13px] text-peri-300">
        Se necesitan al menos tres métricas registradas para trazar el perfil.
      </p>
    );
  }

  const cx = size / 2;
  const cy = size / 2 + 4;
  const radius = size / 2 - 58;
  const stepAngle = 360 / axes;

  const point = (index, fraction) => {
    const angle = ((-90 + index * stepAngle) * Math.PI) / 180;
    return [cx + Math.cos(angle) * radius * fraction, cy + Math.sin(angle) * radius * fraction];
  };

  const polygon = (series) =>
    series
      .map((value, index) => point(index, Math.max(0, Math.min(100, value ?? 0)) / 100).join(","))
      .join(" ");

  // Axis labels are real metric names in Spanish ("Sprints Realizados"), which
  // extend well past the polygon on the left and right. The viewBox is bled
  // horizontally rather than the labels truncated, so the chart stays readable
  // instead of showing "rints Realizado".
  const bleed = 74;

  return (
    <svg
      viewBox={`${-bleed} 0 ${size + bleed * 2} ${size + 8}`}
      width="100%"
      role="img"
      aria-label={`Perfil de ${axes} métricas frente al promedio del grupo`}
    >
      {[0.25, 0.5, 0.75, 1].map((fraction) => (
        <polygon
          key={fraction}
          points={Array.from({ length: axes }, (_, i) => point(i, fraction).join(",")).join(" ")}
          fill="none"
          stroke="#d7dff2"
        />
      ))}
      {Array.from({ length: axes }, (_, i) => {
        const [x, y] = point(i, 1);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#d7dff2" />;
      })}

      {reference.length === axes && (
        <polygon
          points={polygon(reference)}
          fill="rgba(174,191,228,.35)"
          stroke="#7692d1"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      )}
      <polygon
        points={polygon(values)}
        fill="rgba(66,116,217,.28)"
        stroke="#2351ac"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      {values.map((value, index) => {
        const [x, y] = point(index, Math.max(0, Math.min(100, value ?? 0)) / 100);
        return <circle key={index} cx={x} cy={y} r="4" fill="#2351ac" />;
      })}

      {labels.map((label, index) => {
        const [x, y] = point(index, 1.26);
        return (
          <text
            key={label}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="11"
            fill="#16336d"
            fontFamily="Poppins, sans-serif"
          >
            {label}
          </text>
        );
      })}
    </svg>
  );
};

export default Radar;
